/**
 * The ONE traversal of process structure — over the resolved `ProcessModel` IR.
 *
 * Every consumer (stubs, validation, diff, LSP, graph) reads the IR through
 * these helpers instead of re-walking the raw `ProcessStep` AST. That's the
 * point of the IR: one place knows how to recurse process flow, so a new
 * construct is handled once (here + the builder) rather than in N hand-mirrored
 * `walkSimple`/`visitSteps` copies that drift out of lock-step.
 */

import type {
  Flow,
  ProcessNode,
  CallHop,
  CallNode,
  Owner,
  BranchArm,
  SelectCase,
  CatchClause,
  ParallelBody,
  ReversibleItem,
  ReversibleForward,
  UnwindAction,
} from "./process-model.js";
import type { QualifiedName } from "./types.js";

/** Visit every node in a flow, recursing ALL containers, in document order.
 *  `call`/`note`/`await`/`terminator`/`reentry`/`splice`/`description` are
 *  leaves; the rest recurse. Reversible forward/unwind `block`s are transparent
 *  (their body is visited; the block node itself is not a `ProcessNode`). */
export function forEachNode(flow: Flow | undefined, visit: (node: ProcessNode) => void): void {
  for (const n of flow ?? []) {
    visit(n);
    switch (n.kind) {
      case "branch":
        for (const a of n.arms) forEachNode(a.body, visit);
        forEachNode(n.elseArm?.body, visit);
        break;
      case "select":
        for (const c of n.cases) forEachNode(c.body, visit);
        break;
      case "loop":
        forEachNode(n.body, visit);
        break;
      case "retry":
        forEachNode(n.attempt, visit);
        for (const c of n.catches) forEachNode(c.body, visit);
        forEachNode(n.elseBody, visit);
        break;
      case "guard":
        forEachNode(n.attempt, visit);
        for (const c of n.catches) forEachNode(c.body, visit);
        break;
      case "parallel":
        if (n.body.form === "fanOut") forEachNode(n.body.body, visit);
        else for (const b of n.body.branches) forEachNode(b.body, visit);
        // v0.9.0 §2.2: the await-clause timeout branch is a real flow.
        forEachNode(n.awaitClause?.elseBody, visit);
        break;
      case "reversible":
        for (const it of n.items) {
          const fwd = it.forward;
          if (fwd) forEachNode(fwd.kind === "block" ? fwd.body : [fwd], visit);
          const a = it.unwind?.action;
          if (a) forEachNode(a.kind === "block" ? a.body : [a], visit);
        }
        break;
      case "owner_block":
        forEachNode(n.body, visit);
        break;
      default:
        break;
    }
  }
}

/** Visit every SIBLING LIST in a process, outermost first. The unit `|` fan and
 *  `\` snake resolve against (§4.13.4 rule 1: a body boundary is where both
 *  glyphs stop), so anything reasoning about what a glyph line is written
 *  against — an edit that would re-anchor it — walks flows, not nodes. */
export function forEachFlow(flow: Flow | undefined, visit: (flow: Flow) => void): void {
  if (!flow) return;
  visit(flow);
  for (const n of flow) {
    switch (n.kind) {
      case "branch":
        for (const a of n.arms) forEachFlow(a.body, visit);
        forEachFlow(n.elseArm?.body, visit);
        break;
      case "select":
        for (const c of n.cases) forEachFlow(c.body, visit);
        break;
      case "loop":
        forEachFlow(n.body, visit);
        break;
      case "retry":
        forEachFlow(n.attempt, visit);
        for (const c of n.catches) forEachFlow(c.body, visit);
        forEachFlow(n.elseBody, visit);
        break;
      case "guard":
        forEachFlow(n.attempt, visit);
        for (const c of n.catches) forEachFlow(c.body, visit);
        break;
      case "parallel":
        if (n.body.form === "fanOut") forEachFlow(n.body.body, visit);
        else for (const b of n.body.branches) forEachFlow(b.body, visit);
        forEachFlow(n.awaitClause?.elseBody, visit);
        break;
      case "reversible": {
        // The item forwards are ONE sibling list — the builder threads the
        // anchor across them, so a `|` reads the previous item's head and a `\`
        // its tail. Visiting each as its own singleton flow would hide every
        // cross-item glyph from anything reasoning about what a line is written
        // against. A CHAINED forward is a synthetic wrapper around one line, so
        // it contributes the call inside it; an AUTHORED block is a body of its
        // own and is recursed into separately.
        const siblings: ProcessNode[] = [];
        for (const it of n.items) {
          const fwd = it.forward;
          if (!fwd) continue;
          if (fwd.kind !== "block") siblings.push(fwd);
          else if (fwd.chain) siblings.push(...fwd.body);
          else forEachFlow(fwd.body, visit);
        }
        if (siblings.length > 0) visit(siblings);
        for (const it of n.items) {
          // An `unwind` is not a sibling of the items around it (rule 5).
          const a = it.unwind?.action;
          if (a) forEachFlow(a.kind === "block" ? a.body : [a], visit);
        }
        break;
      }
      case "owner_block":
        forEachFlow(n.body, visit);
        break;
      default:
        break;
    }
  }
}

/** Visit every call HOP (one per edge / walk step) with its owning CallNode. */
export function forEachCallHop(
  flow: Flow | undefined,
  fn: (hop: CallHop, node: CallNode) => void,
): void {
  forEachNode(flow, (n) => {
    if (n.kind === "call") for (const h of n.hops) fn(h, n);
  });
}

/** All names bound by a `name = caller > callee` step (process-wide, legal
 *  callers — never modules). */
export function collectBindNames(flow: Flow | undefined): Set<string> {
  const out = new Set<string>();
  forEachNode(flow, (n) => {
    if (n.kind === "call" && n.bind) out.add(n.bind.name);
  });
  return out;
}

/**
 * Substitute a subprocess's `on Caller` template parameter with the caller bound
 * at a `do` site, over the resolved IR `Flow` (`Orders do X` → `Orders` replaces
 * `Caller` throughout X's body). The IR analog of the raw-AST
 * `substituteTemplateCaller` (process-refs.ts), used by the step-emitter to
 * inline a splice identically to how graph/walker inline today.
 *
 * Rewrites exactly what the raw version does — and nothing more (container owners
 * never surface in emitted output, so they are left untouched, matching the raw):
 *   - every `CallHop.caller` equal to `[param]` (covers chain hops + the
 *     resolver-filled owner-omitted heads) → `replacement`;
 *   - the resolved `owner` of call/note/await nodes (the actor/caller lane) when
 *     it is `[param]`;
 *   - a nested `do`'s explicit `templateCaller` when it is `[param]` (pass-through).
 * Pure; returns a new `Flow` (structural copy of touched nodes only).
 */
export function substituteTemplateCallerIR(
  flow: Flow | undefined,
  param: string,
  replacement: QualifiedName,
): Flow {
  const subQN = (q: QualifiedName): QualifiedName =>
    q.length === 1 && q[0] === param ? replacement : q;
  const subOwner = (o: Owner): Owner =>
    o.resolved && o.resolved.length === 1 && o.resolved[0] === param
      ? { ...o, resolved: replacement }
      : o;
  const fl = (f: Flow | undefined): Flow | undefined => (f ? f.map(node) : f);
  const arm = (a: BranchArm): BranchArm => ({ ...a, body: fl(a.body) });
  const sCase = (c: SelectCase): SelectCase => ({ ...c, body: fl(c.body) });
  const cat = (c: CatchClause): CatchClause => ({ ...c, body: fl(c.body) });
  const pBody = (b: ParallelBody): ParallelBody =>
    b.form === "fanOut"
      ? { ...b, body: fl(b.body) }
      : { ...b, branches: b.branches.map((br) => ({ ...br, body: fl(br.body) ?? br.body })) };
  const forward = (f: ReversibleForward): ReversibleForward =>
    f.kind === "block" ? { ...f, body: fl(f.body) ?? f.body } : (node(f) as ReversibleForward);
  const unwindAction = (a: UnwindAction): UnwindAction =>
    a.kind === "block"
      ? { ...a, body: fl(a.body) ?? a.body }
      : a.kind === "call" || a.kind === "splice"
        ? (node(a) as UnwindAction)
        : a;
  const item = (it: ReversibleItem): ReversibleItem => ({
    ...it,
    forward: it.forward ? forward(it.forward) : it.forward,
    unwind: it.unwind ? { ...it.unwind, action: unwindAction(it.unwind.action) } : it.unwind,
  });

  function node(n: ProcessNode): ProcessNode {
    switch (n.kind) {
      case "call":
        return { ...n, owner: subOwner(n.owner), hops: n.hops.map((h) => ({ ...h, caller: subQN(h.caller) })) };
      case "note":
      case "await":
        return { ...n, owner: subOwner(n.owner) };
      case "branch":
        return { ...n, arms: n.arms.map(arm), elseArm: n.elseArm ? arm(n.elseArm) : n.elseArm };
      case "select":
        return { ...n, cases: n.cases.map(sCase) };
      case "loop":
        return { ...n, body: fl(n.body) };
      case "retry":
        return { ...n, attempt: fl(n.attempt) ?? n.attempt, catches: n.catches.map(cat), elseBody: fl(n.elseBody) };
      case "guard":
        return { ...n, attempt: fl(n.attempt) ?? n.attempt, catches: n.catches.map(cat) };
      case "parallel":
        return { ...n, body: pBody(n.body) };
      case "reversible":
        return { ...n, items: n.items.map(item) };
      case "owner_block":
        return { ...n, body: fl(n.body) ?? n.body };
      case "splice":
        return n.templateCaller && n.templateCaller.form === "explicit"
          ? { ...n, templateCaller: subOwner(n.templateCaller) }
          : n;
      // reentry / terminator / description carry no caller to substitute.
      default:
        return n;
    }
  }

  return (flow ?? []).map(node);
}
